import { Injectable } from '@angular/core';

// Shape expected by PieComponent (Syncfusion accumulation chart)
export interface SessionChartData {
  x: string;
  y: number;
  text: string;
  fill: string;
}

export interface CheatingStats {
  tabSwitchCount: number;
  focusLossCount: number;
  fullscreenExitCount: number;
}

export interface QuestionCounts {
  correctAnswers: number | null;
  wrongAnswers: number | null;
  totalQuestions: number | null;
  unansweredQuestions: number | null;
}

export interface PieChartSegment {
  count: number;
  label: string;
  fill: string;
}

/**
 * Single source of truth for all session result display logic.
 * Used by both the student result page and the teacher session detail page.
 */
@Injectable({ providedIn: 'root' })
export class SessionResultMapperService {

  /**
   * Compute answer counts.
   * Priority: studentAnswers[] → flat API fields → questionDetails[] → null.
   */
  computeAnswerCounts(raw: {
    correctAnswers?: number | null;
    wrongAnswers?: number | null;
    totalQuestions?: number | null;
    unansweredQuestions?: number | null;
    studentAnswers?: Array<{ isCorrect?: boolean }>;
    questionDetails?: Array<{ isCorrect?: boolean }>;
  }): QuestionCounts {
    // Prefer studentAnswers as the authoritative source
    const studentAnswers = raw.studentAnswers ?? [];
    const details        = raw.questionDetails ?? [];

    const sourceList = studentAnswers.length > 0 ? studentAnswers : details;
    const correctFromSource = sourceList.filter((q) => q.isCorrect === true).length;
    const wrongFromSource   = sourceList.filter((q) => q.isCorrect === false).length;
    const totalFromSource   = sourceList.length;

    const correctAnswers =
      raw.correctAnswers != null ? raw.correctAnswers
      : totalFromSource > 0     ? correctFromSource
      : null;
    const wrongAnswers =
      raw.wrongAnswers != null ? raw.wrongAnswers
      : totalFromSource > 0    ? wrongFromSource
      : null;
    const totalQuestions =
      raw.totalQuestions != null ? raw.totalQuestions
      : totalFromSource > 0     ? totalFromSource
      : null;
    const unansweredQuestions =
      raw.unansweredQuestions != null ? raw.unansweredQuestions
      : (totalQuestions != null && correctAnswers != null && wrongAnswers != null)
        ? Math.max(0, totalQuestions - correctAnswers - wrongAnswers)
      : null;

    return { correctAnswers, wrongAnswers, totalQuestions, unansweredQuestions };
  }

  /**
   * Extract cheating stats from flat fields, metadata, or sessionMetadata.
   * Priority: flat → metadata.* → sessionMetadata.* → 0.
   */
  extractCheatingStats(raw: any): CheatingStats {
    const meta = raw?.metadata ?? raw?.sessionMetadata ?? {};
    return {
      tabSwitchCount:
        raw.tabSwitchCount      != null ? raw.tabSwitchCount
        : meta.tabSwitchCount   != null ? meta.tabSwitchCount
        : meta.tabSwitches      != null ? meta.tabSwitches
        : 0,
      focusLossCount:
        raw.focusLossCount      != null ? raw.focusLossCount
        : meta.focusLossCount   != null ? meta.focusLossCount
        : meta.focusLosses      != null ? meta.focusLosses
        : 0,
      fullscreenExitCount:
        raw.fullscreenExitCount     != null ? raw.fullscreenExitCount
        : meta.fullscreenExitCount  != null ? meta.fullscreenExitCount
        : meta.fullscreenExits      != null ? meta.fullscreenExits
        : 0,
    };
  }

  /**
   * Build pie/donut chart data for PieComponent.
   * Segments with count === 0 are omitted automatically.
   */
  buildPieChartData(segments: PieChartSegment[]): SessionChartData[] {
    return segments
      .filter((s) => s.count > 0)
      .map((s) => ({ x: s.label, y: s.count, text: `${s.count}`, fill: s.fill }));
  }

  /**
   * CSS utility class for a percentage score value.
   * Mirrors the logic used on the student result page.
   */
  getScoreColorClass(percentage: number | null | undefined): string {
    if (percentage == null) return 'text-muted';
    if (percentage >= 80)   return 'text-success';
    if (percentage >= 60)   return 'text-info';
    if (percentage >= 40)   return 'text-warning';
    return 'text-danger';
  }
}
